/* eslint-disable max-lines */
import {
  cn
} from 'shared/lib/utils';
import {
  channelService,
  opcodeAssistantService,
  outlookConfigService
} from 'shared/api';
import {
  Button
} from 'shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from 'shared/components/ui/dialog';
import {
  Input
} from 'shared/components/ui/input';
import {
  Label
} from 'shared/components/ui/label';
import {
  Context
} from 'shared/context/GlobalState';
import {
  CheckCircle, MailIcon
} from 'lucide-react';
import React, {
  useContext,
  useEffect,
  useState
} from 'react';
import {
  useParams,
  useSearchParams
} from 'react-router-dom';
import {
  server
} from 'shared/utils/httpsRequest';
import WhatsAppSetup from './WhatsApp/WhatsAppSetup';
import VoiceSetup from './Voice/VoiceSetup';
import AssistantSelect from './AssistantSelect';
import {
  Combobox,
  NewCombobox
} from 'shared/components/ui/combobox';
import MessengerSetup from './Messenger/MessengerSetup';
import WebChatSetup from './WebChatSetup';
import {
  ChannelSetupLayout
} from './ChannelSetupLayout';

import {
  RedisChannelAdd,
  Channel,
  ChannelData
} from '../types/builderChannel.types.js';
import {
  ChannelTypeSelection
} from './ChannelTypeSelection';
import SmtpImapSetup from './Email/SmtpImapSetup';
import EmailModeSettings, {
  EmailMode,
  EmailReplyBehavior,
  emailModeToFields
} from './EmailModeSettings';

interface AuthenticateEmailProps {
  isOpen: boolean;
  setOpenEmailModal: (open: boolean) => void;
  setEmailChannels: React.Dispatch<React.SetStateAction<Channel[]>>;
  onCreated?: (channel: Channel) => void;
  defaultAssistantId?: string;
}

interface NewChannelObj extends RedisChannelAdd {
  isAssistantDisabled?: boolean;
}

interface Assistant {
  _id: string;
  name: string;
}

export default function AuthenticateEmail({
  isOpen,
  setOpenEmailModal,
  setEmailChannels,
  onCreated,
  defaultAssistantId,
}: AuthenticateEmailProps) {
  const [newChannel, setNewChannel] = useState<string>('');
  const [mountKey, setMountKey] = useState<number>(0);
  const [disableSelect, setDisableSelect] = useState(false);

  // Wrap setEmailChannels so onCreated fires whenever any setup form adds a channel.
  const setEmailChannelsWithCallback: React.Dispatch<React.SetStateAction<Channel[]>> = (updater) => {
    setEmailChannels((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const added = next.find((c) => !prev.some((p) => p._id === c._id));
      if (added) onCreated?.(added);
      return next;
    });
  };

  const [searchParams] = useSearchParams();
  const success = searchParams.get('email-auth-success');

  useEffect(() => {
    if (isOpen) {
      setMountKey(prev => prev + 1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (success === 'true') {
      setDisableSelect(true);
      setNewChannel('Email');
    }
  }, [success]);

  const seedObj = (defaultAssistantId ? {
    assistant_id: defaultAssistantId,
    isAssistantDisabled: true
  } : {}) as unknown as RedisChannelAdd;

  const getType = () => {
    switch (newChannel) {
      case 'Email':
        return (
            <EmailSetup
            key={mountKey}
            setOpenEmailModal={setOpenEmailModal}
            setEmailChannels={setEmailChannelsWithCallback}
            onBack={() => setNewChannel('')}
            newChannelObj={seedObj}
          />
        );
      case 'Web Chat':
        return (
            <WebChatSetup
            key={mountKey}
            setOpenEmailModal={setOpenEmailModal}
            setEmailChannels={setEmailChannelsWithCallback}
            onBack={() => setNewChannel('')}
            newChannelObj={seedObj}
          />
        );
      case 'WhatsApp':
        return (
            <WhatsAppSetup
            key={mountKey}
            setOpenEmailModal={setOpenEmailModal}
            setEmailChannels={setEmailChannelsWithCallback}
            onBack={() => setNewChannel('')}
            newChannelObj={seedObj}
          />
        );
      case 'Messenger':
        return (
            <MessengerSetup
            key={mountKey}
            setOpenEmailModal={setOpenEmailModal}
            setEmailChannels={setEmailChannelsWithCallback}
            onBack={() => setNewChannel('')}
            newChannelObj={seedObj}
          />
        );
      case 'Voice':
        return (
            <VoiceSetup
            key={mountKey}
            setOpenEmailModal={setOpenEmailModal}
            setEmailChannels={setEmailChannelsWithCallback}
            onBack={() => setNewChannel('')}
            newChannelObj={seedObj}
          />
        );
      default:
        return null;
    }
  };

  return (
      <Dialog open={isOpen} onOpenChange={setOpenEmailModal}>
          <DialogContent className="p-0 sm:max-w-[920px] max-w-none overflow-y-auto max-h-[95svh] sm:max-h-[95vh]">
              {!newChannel && (
              <DialogHeader className="p-6">
                  <DialogTitle>Select Channel Type</DialogTitle>
                  <DialogDescription>
                      Choose a channel type to get started connecting your enterprise.
                  </DialogDescription>
              </DialogHeader>
        )}
              <div className={cn(
          newChannel ? 'p-0' : 'px-6 py-4'
        )}>
                  {!newChannel ? (
                      <ChannelTypeSelection
              onSelect={(type) => setNewChannel(type)}
              selectedType={newChannel}
              disabled={disableSelect}
            />
          ) : (
              <div className="w-full h-full">
                  {getType()}
              </div>
          )}
              </div>
          </DialogContent>
      </Dialog>
  );
}

interface NewChannelInputProps {
  newChannelObj: NewChannelObj;
  setNewChannelObj?: React.Dispatch<React.SetStateAction<NewChannelObj>>;
  channelType: string | null;
  isUpdate?: boolean;
  setOpenEmailModal?: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmit?: (channelObj: ChannelData) => Promise<void>;
}

export const NewChannelInput = ({
  newChannelObj,
  setNewChannelObj,
  channelType
}: NewChannelInputProps) => {

  const [assistants, setAssistants] = useState<Assistant[]>([{
    _id: '',
    name: 'None'
  }]);
  const [assistantsLoading, setAssistantsLoading] = useState(true);

  const {
    oragID, subOragID
  } = useParams<{ oragID: string; subOragID: string }>();

  const handleChange = <K extends keyof NewChannelObj>(value: NewChannelObj[K], type: K) => {
    setNewChannelObj(prev => {
      const updated = {
        ...prev,
        [type]: value
      } as unknown as NewChannelObj;
      return updated;
    });
  };

  useEffect(() => {
    setAssistantsLoading(true);
    opcodeAssistantService.listOpcodeAssistants(oragID, subOragID)
      .then((res) => {
        const mapped = res.data.map((item: any) => ({
          _id: item._id,
          name: String(item.title ?? '')
        }));
        setAssistants(prev => ([...prev, ...mapped]));
      })
      .catch((err: unknown) => {
        console.error('listOpcodeAssistants error:', err);
      })
      .finally(() => {
        setAssistantsLoading(false);
      });
  }, [oragID, subOragID]);

  return (
      <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4">
              <Label htmlFor="channel-type">Address</Label>
              <Input
          value={newChannelObj.address ?? ''}
          disabled={Boolean(newChannelObj.isAssistantDisabled) || !channelType}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(e.target.value as NewChannelObj['address'], 'address')
          }
        />

              <Input
          disabled={!channelType}
          value={newChannelObj.topic ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange(e.target.value as NewChannelObj['topic'], 'topic')
          }
          placeholder="Enquires, Reservation, ..."
        />

              <Combobox
          className=""
          buttonProps={{}}
          comboboxWidth="w-full"
          items={assistants}
          setter={(value: Assistant) =>
            handleChange(value._id as NewChannelObj['assistant_id'], 'assistant_id')
          }
          selectedValue={assistants.find(a => a._id === newChannelObj.assistant_id) ?? undefined}
          disabled={assistantsLoading || !channelType}
        />

          </div>
      </div>
  );
};

interface EmailSetupProps {
  setOpenEmailModal: (open: boolean) => void;
  setEmailChannels: React.Dispatch<React.SetStateAction<Channel[]>>;
  onBack: () => void;
  newChannelObj?: { assistant_id?: string; isAssistantDisabled?: boolean };
}

const EmailSetup = ({
  setOpenEmailModal,
  setEmailChannels,
  onBack,
  newChannelObj,
}: EmailSetupProps) => {
  const {
    oragID, subOragID
  } = useParams<{ oragID: string; subOragID: string }>();
  const {
    addNewNotifcation
  } = useContext(Context);

  const [searchParams, setSearchParams] = useSearchParams();
  const success = searchParams.get('email-auth-success');
  const emailParam = searchParams.get('email');

  // Which transport backs this mailbox. Microsoft 365 keeps the OAuth flow
  // below untouched; SMTP/IMAP (the bex-mail exchange) has no redirect and
  // renders its own screen. Returning from the OAuth redirect implies M365.
  const [emailProvider, setEmailProvider] = useState<'' | 'outlook' | 'smtp'>(
    success === 'true' ? 'outlook' : '',
  );

  const [setupStep, setSetupStep] = useState(1);
  // Mode is chosen BEFORE OAuth (send-only authenticates with subscription=skip),
  // so it must survive the OAuth redirect: persisted to sessionStorage on
  // authenticate, restored on remount. The stored value is timestamped and
  // honored for 15 minutes only — without the expiry an ABANDONED attempt
  // (backing out at the Microsoft screen) would silently pre-select its mode
  // for the next channel setup in the same tab. Fallback when storage is
  // empty: a temp auth without subscription_id can only be a send-only auth.
  const storedModeChoice = ((): { mode: EmailMode; reply: EmailReplyBehavior } | null => {
    try {
      const raw = sessionStorage.getItem('bex-email-channel-mode');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (typeof parsed?.ts !== 'number' || Date.now() - parsed.ts > 15 * 60 * 1000) return null;
      if (parsed.mode !== 'listen' && parsed.mode !== 'send_only' && parsed.mode !== 'send_receive') return null;
      return {
        mode: parsed.mode,
        reply: parsed.reply === 'auto' ? 'auto' : 'tool',
      };
    } catch { return null; }
  })();
  const [emailMode, setEmailMode] = useState<EmailMode>(storedModeChoice?.mode ?? 'listen');
  const [replyBehavior, setReplyBehavior] = useState<EmailReplyBehavior>(storedModeChoice?.reply ?? 'tool');
  const [fetchEmailLoading, setFetchEmailLoading] = useState(false);
  const [outlookConfigured, setOutlookConfigured] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [channelData, setChannelData] = useState<{
    type: string;
    address: string;
    topic: string;
    assistantId: string;
    access_token?: string;
    refresh_token?: string;
    subscription_id?: string;
  }>({
    type: 'email',
    address: '',
    topic: '',
    assistantId: newChannelObj?.assistant_id || '',
  });
  useEffect(() => {
    setConfigLoading(true);
    outlookConfigService.getOutlookConfig(oragID)
      .then((res: any) => {
        const outlook = res.data?.outlook;
        setOutlookConfigured(Boolean(outlook?.clientId && outlook?.clientSecret));
      })
      .catch(() => setOutlookConfigured(false))
      .finally(() => setConfigLoading(false));
  }, [oragID]);

  useEffect(() => {
    if (success === 'true' && emailParam) {
      setFetchEmailLoading(true);
      channelService.getTempEmail(oragID, subOragID, emailParam.toLowerCase())
        .then((res: any) => {
          const {
            email_auth, access_token, refresh_token, subscription_id
          } = res.data;
          setChannelData(prev => ({
            ...prev,
            address: email_auth ?? emailParam,
            ...(access_token && {
 access_token
}),
            ...(refresh_token && {
 refresh_token
}),
            ...(subscription_id && {
 subscription_id
}),
          }));
          // No subscription on the temp auth => the auth ran with subscription=skip,
          // which only the send-only path does. Corrects a lost/cleared storage value.
          if (!subscription_id) setEmailMode('send_only');
          setSetupStep(2);
          // Temp auth is now consumed (get-email-auth-temp is a one-shot
          // findOneAndDelete). Clear the OAuth return params so a remount can't
          // re-fetch it (which would 404) and so the URL is clean.
          setSearchParams({
 tab: 'channels'
}, {
 replace: true
});
        })
        .catch((err: unknown) => {
          console.error('getTempEmail error:', err);
          addNewNotifcation('Failed to retrieve authentication details. Please authenticate again.', 'danger');
        })
        .finally(() => setFetchEmailLoading(false));
    }
  }, [success, emailParam, oragID, subOragID]);

  const handleSubmit = async () => {
    setBtnLoading(true);
    try {
      const modeFields = emailModeToFields(emailMode, replyBehavior);
      const payload = {
        org_id: oragID || '',
        sub_org_id: subOragID || '',
        address: channelData.address,
        topic: channelData.topic,
        type: channelData.type,
        provider: 'outlook',
        assistant_id: channelData.assistantId || '',
        ...modeFields,
        ...(channelData.subscription_id ? {
 subscription_id: channelData.subscription_id
} : {}),
        // The Graph tokens MUST land on the channel record: FEO reads
        // channel.access_token/refresh_token for every Graph call, and a
        // send-only channel has no subscription lifecycle to attach them later.
        ...(channelData.access_token ? {
 access_token: channelData.access_token
} : {}),
        ...(channelData.refresh_token ? {
 refresh_token: channelData.refresh_token
} : {}),
      };
      const res = await channelService.addChannel(payload);
      try {
        sessionStorage.removeItem('bex-email-channel-mode');
        sessionStorage.removeItem('bex-email-channel-reply');
      } catch { /* storage unavailable — nothing to clean */ }
      addNewNotifcation('Channel added successfully', 'success');
      setOpenEmailModal(false);
      setEmailChannels(prev => [res.data, ...prev]);
    } catch (error: any) {
      const status = error.response?.status;
      if (status === 409) {
        addNewNotifcation('Channel already exists', 'danger');
      } else if (status === 403) {
        addNewNotifcation(
          error?.response?.data?.message || 'Resource limit reached. You cannot create more channels under your current plan.',
          'danger'
        );
      } else {
        addNewNotifcation('Something went wrong', 'danger');
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const isButtonDisabled = () => setupStep !== 2 || !channelData.topic.trim() || !channelData.assistantId;

  if (fetchEmailLoading) {
    return (
        <div className="w-full max-w-[850px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Fetching account details...</p>
        </div>
    );
  }

  if (emailProvider === 'smtp') {
    return (
        <SmtpImapSetup
        setOpenEmailModal={setOpenEmailModal}
        setEmailChannels={setEmailChannels}
        onBack={() => setEmailProvider('')}
        newChannelObj={newChannelObj}
      />
    );
  }

  if (emailProvider === '') {
    return (
        <div className="w-full p-8 flex flex-col gap-5">
            <div className="space-y-1">
                <h4 className="text-lg font-bold text-foreground">How is this mailbox hosted?</h4>
                <p className="text-sm text-muted-foreground">
                    Both connect a real mailbox to the platform — they differ only in how it signs in.
                </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <button
            type="button"
            onClick={() => setEmailProvider('outlook')}
            className="text-left border border-border rounded-xl p-5 hover:border-primary hover:bg-primary/5 transition-colors"
          >
                    <h5 className="text-sm font-semibold text-foreground mb-1">Microsoft 365 / Outlook</h5>
                    <p className="text-xs text-muted-foreground">
                        Sign in with Microsoft. Best when the mailbox lives in Exchange Online.
                    </p>
                </button>
                <button
            type="button"
            onClick={() => setEmailProvider('smtp')}
            className="text-left border border-border rounded-xl p-5 hover:border-primary hover:bg-primary/5 transition-colors"
          >
                    <h5 className="text-sm font-semibold text-foreground mb-1">SMTP / IMAP</h5>
                    <p className="text-xs text-muted-foreground">
                        Any other mail host — cPanel/Vodien, Gmail app passwords, self-hosted. Uses the
                        mailbox&apos;s server addresses and a stored sign-in.
                    </p>
                </button>
            </div>
            <div>
                <Button variant="ghost" size="sm" onClick={onBack}>Back</Button>
            </div>
        </div>
    );
  }

  return (
      <ChannelSetupLayout
      onCancel={() => setOpenEmailModal(false)}
      onSubmit={handleSubmit}
      submitDisabled={isButtonDisabled()}
      loading={btnLoading}
      onBack={onBack}
      title="Configure Email Channel"
      isDashboard={true}
    >
          <div className="flex flex-col sm:flex-row w-full">
              <aside className="sm:w-[320px] shrink-0 bg-muted/40 border-b sm:border-b-0 sm:border-r border-border p-6 flex flex-col gap-5">
                  <div className="space-y-1">
                      <div className="inline-flex items-center gap-2 px-2 py-1 bg-primary/10 rounded-md text-primary mb-1">
                          <MailIcon size={12} />
                          <span className="text-[10px] font-bold uppercase tracking-wider">Channel Configuration</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground px-1">Configure Instance</h3>
                  </div>
                  <div className="flex flex-col gap-4">
                      <div className="flex flex-col gap-1">
                          <Label className="text-[11px] font-semibold text-muted-foreground ml-1">
                              Display Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                value={channelData.topic}
                onChange={(e) => setChannelData(prev => ({
                  ...prev,
                  topic: e.target.value
                }))}
                placeholder="Customer Support"
                className="h-9 text-xs"
                disabled={setupStep !== 2}
              />
                      </div>
                      {!newChannelObj?.isAssistantDisabled && (
                      <div className="flex flex-col gap-1.5">
                          <Label className="text-[11px] font-semibold text-muted-foreground ml-1">
                              AI Assistant
                          </Label>
                          <AssistantSelect
                className="mt-0.5"
                value={channelData.assistantId}
                onChange={(value: string) => setChannelData(prev => ({
                  ...prev,
                  assistantId: value
                }))}
                disabled={setupStep !== 2}
              />
                      </div>
                      )}
                  </div>
              </aside>

              <main className="flex-grow bg-background p-8 flex flex-col items-center justify-center">
                  <div className="w-full max-w-sm space-y-6 text-center animate-in fade-in slide-in-from-bottom-2 duration-500">
                      <div className="w-16 h-16 bg-primary/10 text-primary rounded-2xl flex items-center justify-center mx-auto">
                          <MailIcon size={32} />
                      </div>
                      {setupStep === 1 ? (
                          <>
                              <div className="space-y-2">
                                  <h4 className="text-lg font-bold text-foreground">Authenticate Outlook</h4>
                                  <p className="text-sm text-muted-foreground">
                                      Connect your Microsoft Outlook email account to enable email channel functionality.
                                  </p>
                                  {!outlookConfigured && !configLoading && (
                                      <p className="text-xs text-red-500 mt-2">
                                          Outlook credentials not configured. Please configure them before proceeding.
                                      </p>
                                  )}
                              </div>
                              <EmailModeSettings
                  mode={emailMode}
                  replyBehavior={replyBehavior}
                  onModeChange={setEmailMode}
                  onReplyBehaviorChange={setReplyBehavior}
                />
                              <Button
                  disabled={!outlookConfigured || configLoading}
                  onClick={() => {
                    try {
                      sessionStorage.setItem('bex-email-channel-mode', JSON.stringify({
                        mode: emailMode,
                        reply: replyBehavior,
                        ts: Date.now(),
                      }));
                    } catch { /* storage unavailable — subscription_id fallback covers send-only */ }
                    // Send-only mailboxes get no Graph inbound subscription.
                    const skip = emailMode === 'send_only' ? '&subscription=skip' : '';
                    window.location.href = `${server}/outlook/auth/microsoft?organizationId=${oragID}&subOrganizationId=${subOragID}${skip}`;
                  }}
                  className="w-full"
                >
                                  <MailIcon className="mr-2 h-4 w-4" />
                                  {configLoading ? 'Checking configuration...' : 'Authenticate with Outlook'}
                              </Button>
                          </>
            ) : (
                <div className="space-y-4">
                    <div className="flex items-center justify-center gap-2 text-green-600">
                        <CheckCircle size={20} />
                        <span className="text-sm font-medium">Outlook Connected</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-muted border border-border rounded-lg">
                        <MailIcon size={14} className="text-muted-foreground shrink-0" />
                        <code className="text-[11px] text-muted-foreground font-mono break-all">
                            {channelData.address}
                        </code>
                    </div>
                    <div className="text-left">
                        <EmailModeSettings
                    mode={emailMode}
                    replyBehavior={replyBehavior}
                    onModeChange={setEmailMode}
                    onReplyBehaviorChange={setReplyBehavior}
                    lockedModes={channelData.subscription_id ? ['send_only'] : ['listen', 'send_receive']}
                  />
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Fill in the display name on the left, then submit.
                    </p>
                </div>
            )}
                  </div>
              </main>
          </div>
      </ChannelSetupLayout>
  );
};
